import { useMemo } from 'react';

import { ErrorLike, gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useSelector } from 'react-redux';

import useDialogs from '@/hooks/useDialogs';
import { selectFilters } from '@/modules/analytics/redux/selectors';
import { selectAccountId, selectBrainId, selectDialogId } from '@/redux/session/selectors';
import { getAnalyticsRangeLocal } from '@/util/analytics';
import { getGraphQLSet, isUuid } from '@/util/util';

interface QueryVariables {
  accountId: string;
  startTime: string | null;
  endTime: string | null;
  brainParentIds?: string | null;
}

interface Rows {
  get_unique_tags: { tags: string[] }[];
}

interface UseBrainUniqueTagsOptions {
  skip?: boolean;
  currentTags?: string[];
  searchText?: string;
}

export interface BrainUniqueTagsResult {
  isLoading: boolean;
  error?: ErrorLike;
  availableTags: string[];
}

export const BRAIN_UNIQUE_TAGS = gql`
  query GetBrainUniqueTags(
    $accountId: uuid
    $startTime: timestamp
    $endTime: timestamp
    $brainParentIds: _uuid
  ) {
    get_unique_tags(
      args: {
        account_id: $accountId
        start_time: $startTime
        end_time: $endTime
        brain_parent_ids: $brainParentIds
      }
    ) {
      tags
    }
  }
`;

/**
 * Fetches unique tags from session data and merges them with flow tags and
 * currently selected tags into a deduplicated, sorted, searchable list.
 */
export const useBrainUniqueTags = (options?: UseBrainUniqueTagsOptions): BrainUniqueTagsResult => {
  const accountId = useSelector(selectAccountId);
  const brainId = useSelector(selectBrainId);
  const dialogId = useSelector(selectDialogId);
  const filters = useSelector(selectFilters);
  const { dialogTags } = useDialogs(brainId, dialogId);

  const variables = useMemo(() => {
    const { startDate, endDate } = getAnalyticsRangeLocal(
      filters.startDate || undefined,
      filters.endDate || undefined
    );
    return {
      accountId,
      startTime: startDate,
      endTime: endDate,
      brainParentIds: getGraphQLSet([brainId]),
    };
  }, [accountId, brainId, filters.endDate, filters.startDate]);

  const {
    loading,
    data: rawData,
    error,
  } = useQuery<Rows, QueryVariables>(BRAIN_UNIQUE_TAGS, {
    variables,
    // endDate is intentionally not guarded — a null endDate means an open-ended
    // range ("from startDate to now"), which getAnalyticsRangeLocal handles.
    skip:
      options?.skip ||
      !accountId ||
      !isUuid(brainId) ||
      !filters.startDate ||
      !filters.filtersLoaded,
  });

  const apiTags = useMemo(
    () => rawData?.get_unique_tags?.flatMap((row) => row.tags) ?? [],
    [rawData]
  );

  const availableTags = useMemo(() => {
    const selected = Array.isArray(options?.currentTags) ? options.currentTags : [];
    const flowTags = Array.from(dialogTags || new Set<string>());
    const searchText = options?.searchText ?? '';

    const merged = new Set<string>();

    for (const tag of [...flowTags, ...apiTags, ...selected]) {
      if (typeof tag === 'string' && tag.trim().length > 0) {
        merged.add(tag);
      }
    }

    return Array.from(merged)
      .filter((tag) => tag.toLowerCase().includes(searchText.toLowerCase()))
      .sort((a, b) => a.localeCompare(b));
  }, [apiTags, options?.currentTags, options?.searchText, dialogTags]);

  return { isLoading: loading, error, availableTags };
};
